import React, { memo, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { Divider, Popover, Typography } from 'antd';

import Spin from 'components/Spin';
import Truncate from 'components/Truncate';
import { dateMomentFormat } from 'v2/formatters/date';
import chatService from 'v2/services/chat';

interface IProps {
  isOpen: boolean;
  isFirstOpen: boolean;
  onOpenChange: (open: boolean) => void;
  unreadMessagesCount: number;
  children: React.ReactNode;
}

const MessagesList = memo(({ isOpen, onOpenChange, unreadMessagesCount, isFirstOpen, children }: IProps) => {
  const [messages] = useObservable(() => chatService.getMessages(), []);
  const [isLoading] = useObservable(() => chatService.getLoadingMessages(), []);

  const shouldLoadMessages = useMemo(() => {
    if (!isOpen) {
      return false;
    }

    if (isFirstOpen || unreadMessagesCount > 0) {
      return true;
    }

    return false;
  }, [isFirstOpen, isOpen, unreadMessagesCount]);

  const renderMessages = useMemo(() => {
    if (isLoading) {
      return (
        <div className='min-w-[280px] py-3 text-center'>
          <Spin />
        </div>
      );
    }

    if (!messages?.length) {
      return <div className='p-4 text-center text-sm text-[#333]'>Nenhuma mensagem disponível</div>;
    }

    return messages?.map(chat => {
      const isUnread = chat.not_read_count > 0;
      return (
        <Link key={chat.id} to={'/producer/messages/' + chat.id} onClick={() => onOpenChange(false)}>
          <div className={`flex items-center px-4 py-2.5 text-sm hover:bg-[#f5f5f5] ${isUnread ? 'bg-[#f0f7ff]' : ''}`}>
            <div
              className='relative mr-3 h-[36px] w-[36px] shrink-0 rounded-full border border-[#ddd] bg-[#eee] bg-cover bg-center'
              style={{
                backgroundImage: `url(${chat.outer.avatar || '/img/avatar-default.png'})`
              }}
            >
              {isUnread && (
                <span className='absolute -top-0.5 left-0 z-2 h-3 w-3 rounded-full border-2 border-white bg-[#087922]' />
              )}
              {chat.outer.qualification && (
                <div
                  className='absolute -right-1 -bottom-1 z-2 h-5 w-5 rounded-full bg-white bg-cover bg-center'
                  style={{
                    backgroundImage: `url(https://cdn.eduzzcdn.com/badges/${chat.outer.qualification.badge_elite}${
                      chat.outer.qualification.has_plus ? '-plus' : ''
                    }.svg)`
                  }}
                />
              )}
            </div>
            <div className='min-w-0 flex-1'>
              <div
                className={`mb-0.5 block truncate ${isUnread ? 'font-semibold text-[#222]' : 'font-medium text-[#555]'}`}
              >
                <Truncate count={25} final='...'>
                  {chat.outer.name}
                </Truncate>
              </div>
              <div className={`mb-0.5 block truncate text-[13px] ${isUnread ? 'text-[#555]' : 'text-[#999]'}`}>
                <Truncate count={30} final='...'>
                  {chat.last.content}
                </Truncate>
              </div>
              <div className='text-xs text-[#aaa]'>{dateMomentFormat(chat.last.created_at)}</div>
            </div>
          </div>
        </Link>
      );
    });
  }, [isLoading, messages, onOpenChange]);

  useEffect(() => {
    if (shouldLoadMessages) {
      chatService.loadMessages(4);
    }
  }, [shouldLoadMessages]);

  const content = (
    <div className='min-w-[280px]'>
      <Typography.Title level={5} className='mb-0! py-2 text-center'>
        Mensagens
      </Typography.Title>
      <Divider className='my-0!' />
      {renderMessages}
      <Divider className='my-0!' />
      <Link to={'/producer/messages'} onClick={() => onOpenChange(false)}>
        <span className='block w-full p-2 text-center text-[13px] font-medium text-[#333]'>VER TODAS</span>
      </Link>
    </div>
  );

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange} content={content} trigger='click' placement='bottom'>
      {children}
    </Popover>
  );
});

export default MessagesList;
